import { Component, Input, Output, EventEmitter, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDragDrop, CdkDrag, CdkDropList, CdkDragHandle, moveItemInArray } from '@angular/cdk/drag-drop';
import { ProjectStage } from '../../../shared/models/project.model';
import { Task } from '../../../shared/models/task.model';
import { ListTaskComponent } from './list-task/list-task';
import { ConfirmModalComponent } from '../../../shared/utils/modal-validation/modal-validation';
import { ProjectService } from '../../../shared/services/project.service';
import { ExternalLinkService } from '../../../shared/services/external-link.service';
import { User } from '../../../shared/services/user';

@Component({
  selector: 'app-list-stage-project',
  imports: [CommonModule, FormsModule, ListTaskComponent, ConfirmModalComponent, CdkDrag, CdkDropList, CdkDragHandle],
  templateUrl: './list-stage-project.html',
  styleUrls: ['./list-stage-project.scss']
})
export class ListStageProjectComponent {
  private projectService = inject(ProjectService);
  private externalLinkService = inject(ExternalLinkService);
  userService = inject(User);
  @Input() projectStages: ProjectStage[] = [];
  @Input() allTasks: Task[] = [];
  @Input() encodedHoursByStage: Record<string, number> = {};
  @Input() projectManagerUserIds: string[] = [];
  @Input() projectStartDate?: string;

  @Output() addTask = new EventEmitter<string>();
  @Output() stageChange = new EventEmitter<{ stageId: string, changes: Partial<ProjectStage> }>();
  @Output() stageDeleted = new EventEmitter<string>();
  @Output() editStage = new EventEmitter<ProjectStage>();
  @Output() taskDetailOpen = new EventEmitter<string>();

  editingStageId?: string;
  editedStageName = ''; // Temporary copy for editing
  showConfirm = false;
  stageId?: string;
  confirmMessage = '';
  openMenuId?: string;

  /** Expanded phase ids (task list shown). Phases start collapsed. */
  expandedStages = new Set<string>();

  toggleCollapse(stageId: string, event?: Event) {
    event?.stopPropagation();
    if (this.expandedStages.has(stageId)) this.expandedStages.delete(stageId);
    else this.expandedStages.add(stageId);
  }

  isCollapsed(stageId: string): boolean {
    return !this.expandedStages.has(stageId);
  }

  /** French status label for a phase. */
  getStageStatusLabel(status: string | null | undefined): string {
    switch (status) {
      case 'INPROGRESS': return 'In Progress';
      case 'DONE': return 'Done';
      case 'NOTBEGIN':
      default: return 'New';
    }
  }

  /** Progress bar fill (%) = encoded hours / budget, capped at 100. */
  getStageProgress(stageId: string): number {
    const stage = this.projectStages.find(s => s.projectStageId === stageId);
    const budget = stage?.estimatedHours || 0;
    if (budget <= 0) return 0;
    const pct = (this.getStageEncodedHours(stageId) / budget) * 100;
    return Math.min(100, Math.max(0, pct));
  }

  // Phase links management
  showAddLinkFormForStage?: string;
  newStageLinkUrl = '';
  newStageLinkName = 'Drive';
  newStageLinkType: 'Drive' | 'Other' = 'Drive';

  // Close menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    this.openMenuId = undefined;
  }

  /**
   * The event has to be passed in: reading the global `window.event` instead left
   * the click free to bubble to the document:click listener above whenever the
   * browser did not populate it, which closed the menu in the same tick that
   * opened it.
   */
  toggleMenu(stageId: string, event: Event) {
    event.stopPropagation();
    this.openMenuId = this.openMenuId === stageId ? undefined : stageId;
  }

  getStageStatusClasses(status: string | null | undefined): string {
    switch (status) {
      case 'INPROGRESS':
        return 'bg-blue-100 text-blue-700 focus:ring-blue-400';
      case 'DONE':
        return 'bg-emerald-100 text-emerald-700 focus:ring-emerald-400';
      case 'NOTBEGIN':
      default:
        return 'bg-slate-100 text-slate-600 focus:ring-slate-400';
    }
  }

  getStatusIconClass(status: string | null | undefined): string {
    switch (status) {
      case 'INPROGRESS': return 'text-blue-500';
      case 'DONE': return 'text-emerald-500';
      case 'NOTBEGIN':
      default: return 'text-slate-400';
    }
  }

  getStatusIcon(status: string | null | undefined): string {
    switch (status) {
      case 'INPROGRESS': return 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'; // clock
      case 'DONE': return 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'; // check-circle
      case 'NOTBEGIN':
      default: return 'M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'; // dots
    }
  }

  onStageFieldChange(stageId: string, fieldName: keyof ProjectStage, value: any) {
    // Only allow admin to change stage fields
    if (!this.userService.admin()) return;

    this.stageChange.emit({
      stageId,
      changes: { [fieldName]: value }
    });
  }

  // Stage name editing methods (like project name editing)
  startEditStageName(stage: ProjectStage) {
    if (!this.userService.admin()) return;
    this.editingStageId = stage.projectStageId;
    this.editedStageName = stage.stageName;
    // Pause refresh while editing
    this.projectService.pauseRefresh();
  }

  saveStageName(stageId: string) {
    if (!this.userService.admin()) return;

    if (this.editedStageName.trim() && this.editedStageName.trim() !== this.getStageById(stageId)?.stageName) {
      this.stageChange.emit({
        stageId,
        changes: { stageName: this.editedStageName.trim() }
      });
    }

    this.editingStageId = undefined;
    this.editedStageName = '';
    // Resume refresh after editing
    this.projectService.resumeRefresh();
  }

  cancelEditStageName() {
    this.editingStageId = undefined;
    this.editedStageName = '';
    // Resume refresh after editing
    this.projectService.resumeRefresh();
  }

  getStageById(stageId: string): ProjectStage | undefined {
    return this.projectStages.find(s => s.projectStageId === stageId);
  }

  getStageRemainingTime(stageId: string): number {
    const stage = this.projectStages.find(s => s.projectStageId === stageId);
    if (!stage) return 0;
    const stageEst = stage.estimatedHours || 0;
    const tasksForStage = this.allTasks.filter(t => t.projectStageId === stageId);
    const tasksSum = tasksForStage.reduce((acc, task) => acc + (task.estimatedHours || 0), 0);
    return stageEst - tasksSum;
  }

  getStageEncodedHours(stageId: string): number {
    return this.encodedHoursByStage[stageId] || 0;
  }

  getStageTasksAllocated(stageId: string): number {
    return this.allTasks
      .filter(t => t.projectStageId === stageId)
      .reduce((acc, t) => acc + (t.estimatedHours || 0), 0);
  }

  onAddTask(stageId: string) {
    // All users with project access can add tasks
    this.addTask.emit(stageId);
  }

  deleteStageProject(stageId: string) {
    // Only allow admin to delete stages
    if (!this.userService.admin()) return;

    this.openMenuId = undefined;
    this.stageId = stageId;
    if (!stageId) return;
    this.confirmMessage = 'Are you sure you want to delete this phase and all its tasks?';
    this.showConfirm = true;
  }

  handleConfirm(result: 'ok' | 'cancel') {
    this.showConfirm = false;
    if (result === 'ok' && this.stageId) {
      this.projectService.deleteProjectStage(this.stageId)
        .then(() => {this.stageDeleted.emit(this.stageId)})
        .catch(err => console.error(err));
    }
  }

  // Drag & Drop handler for project stages
  async onStageDrop(event: CdkDragDrop<ProjectStage[]>) {
    // Only allow admin to reorder stages
    if (!this.userService.admin()) return;

    if (event.previousIndex === event.currentIndex) return;

    console.log('🔄 Reordering stages - Before:', this.projectStages.map(s => ({ name: s.stageName, order: s.order })));

    // Reorder locally
    moveItemInArray(this.projectStages, event.previousIndex, event.currentIndex);

    // Update order values for all stages in this list
    this.projectStages.forEach((stage, index) => {
      stage.order = index;
    });

    console.log('🔄 Reordering stages - After:', this.projectStages.map(s => ({ name: s.stageName, order: s.order })));

    // Update order in backend
    const success = await this.projectService.reorderProjectStages(this.projectStages);

    if (success) {
      console.log('✅ Stages reordered successfully');
    } else {
      console.error('❌ Failed to reorder stages');
    }
  }

  onTaskDetailOpen(taskId: string) {
    // Bubble up the event to the parent component
    this.taskDetailOpen.emit(taskId);
  }

  // Check if user can manage stage (admin or project manager)
  canManageStage(stageId: string): boolean {
    return this.userService.admin();
    // Could extend to check if user is project manager
    // return this.userService.admin() || this.projectManagerUserIds.includes(currentUserId);
  }

  // Phase links management methods

  toggleAddStageLink(stageId: string) {
    if (this.showAddLinkFormForStage === stageId) {
      this.showAddLinkFormForStage = undefined;
      this.newStageLinkUrl = '';
      this.newStageLinkName = 'Drive';
      this.newStageLinkType = 'Drive';
    } else {
      this.showAddLinkFormForStage = stageId;
      this.newStageLinkUrl = '';
      this.newStageLinkName = 'Drive';
      this.newStageLinkType = 'Drive';
    }
  }

  onStageLinkTypeChange() {
    console.log('🔄 [onStageLinkTypeChange] Type changed to:', this.newStageLinkType);
    if (this.newStageLinkType === 'Drive') {
      this.newStageLinkName = 'Drive';
      console.log('✅ [onStageLinkTypeChange] Auto-filled name with "Drive"');
    } else {
      this.newStageLinkName = '';
      console.log('✅ [onStageLinkTypeChange] Cleared name for custom input');
    }
  }

  addStageLink(stageId: string) {
    console.log('📎 [addStageLink] Called with:', {
      stageId,
      url: this.newStageLinkUrl,
      name: this.newStageLinkName,
      type: this.newStageLinkType
    });

    if (!this.newStageLinkUrl.trim() || !this.newStageLinkName.trim()) {
      console.warn('⚠️ [addStageLink] Validation failed - missing URL or name');
      return;
    }

    // Basic URL validation
    try {
      new URL(this.newStageLinkUrl.trim());
    } catch {
      alert('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    const stage = this.projectStages.find(s => s.projectStageId === stageId);
    if (!stage) {
      console.error('❌ [addStageLink] Stage not found:', stageId);
      return;
    }

    const currentLinks = stage.links || [];
    const newLink = {
      name: this.newStageLinkName.trim(),
      url: this.newStageLinkUrl.trim()
    };
    const updatedLinks = [...currentLinks, newLink];

    console.log('📎 [addStageLink] Stage:', stage.stageName);
    console.log('📎 [addStageLink] Current links:', currentLinks);
    console.log('📎 [addStageLink] New link object:', newLink);
    console.log('📎 [addStageLink] Updated links array:', updatedLinks);

    this.stageChange.emit({
      stageId,
      changes: { links: updatedLinks }
    });

    console.log('✅ [addStageLink] Event emitted, resetting form');

    this.newStageLinkUrl = '';
    this.newStageLinkName = 'Drive';
    this.newStageLinkType = 'Drive';
    this.showAddLinkFormForStage = undefined;
  }

  removeStageLink(stageId: string, index: number) {
    if (!confirm('Are you sure you want to remove this link?')) return;

    const stage = this.projectStages.find(s => s.projectStageId === stageId);
    if (!stage) return;

    const currentLinks = stage.links || [];
    const updatedLinks = currentLinks.filter((_, i) => i !== index);

    this.stageChange.emit({
      stageId,
      changes: { links: updatedLinks }
    });
  }

  cancelAddStageLink() {
    this.showAddLinkFormForStage = undefined;
    this.newStageLinkUrl = '';
    this.newStageLinkName = 'Drive';
    this.newStageLinkType = 'Drive';
  }

  getLinkDisplayName(url: any): string {
    // If it's an object with name, use that
    if (typeof url === 'object' && url?.name) {
      return url.name;
    }
    // If it's a string URL, extract hostname
    if (typeof url === 'string') {
      try {
        const urlObj = new URL(url);
        return urlObj.hostname.replace('www.', '');
      } catch {
        return url;
      }
    }
    return 'Link';
  }

  // Extract URL from link object or string
  getLinkUrl(link: any): string {
    if (typeof link === 'object' && link?.url) {
      return link.url;
    }
    return typeof link === 'string' ? link : '';
  }

  // Open external link (works in both web and Tauri)
  openExternalLink(url: string, event?: Event): void {
    console.log('🔗 [openExternalLink] Method called');
    console.log('🔗 [openExternalLink] URL received:', url);
    console.log('🔗 [openExternalLink] Event:', event);

    if (url) {
      console.log('✅ [openExternalLink] URL is valid, calling externalLinkService.openExternal');
      console.log('🔗 [openExternalLink] ExternalLinkService instance:', this.externalLinkService);

      try {
        this.externalLinkService.openExternal(url, event);
        console.log('✅ [openExternalLink] externalLinkService.openExternal called successfully');
      } catch (error) {
        console.error('❌ [openExternalLink] Error calling externalLinkService:', error);
      }
    } else {
      console.warn('⚠️ [openExternalLink] No URL provided');
    }
  }
}
